import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import { FileUploadStatusProps } from 'carbon-react/lib/components/file-input';

import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { ExcelUploadPanel } from '../form-elements/excel-upload-panel/excel-upload-panel';
import { ForecastDatasource, ForecastDatasourceProps } from '../form-elements/forecast-datasource';
import { ForecastFormErrors } from '../../types';
import { ForecastFrom, ForecastFromProps } from '../form-elements/forecast-from';
import { ForecastName, ForecastNameProps } from '../form-elements/forecast-name';
import { ForecastSourceForecast, ForecastSourceForecastProps } from '../form-elements/forecast-source-forecast';
import { IconMessage } from '../form-elements/icon-message/icon-message';
import { NewForecastDataSource, OrgOrConForecastListItem } from '../../../dashboard/types';

import styles from '../form-elements/elements.scss';

interface CreateNewBudgetFormProps {
  datasource: NewForecastDataSource | '';
  errors?: ForecastFormErrors;
  excelFileError?: string;
  excelTemplateLink?: string;
  fileUploadStatus?: FileUploadStatusProps;
  forecasts?: OrgOrConForecastListItem[];
  from: string;
  isLoadingExcelTemplate?: boolean;
  maxDate?: string;
  minDate?: string;
  name: string;
  sourceForecast?: string;
  onChangeDatasource: ForecastDatasourceProps['onChange'];
  onChangeFile: (files: FileList) => void;
  onChangeFrom: ForecastFromProps['onChange'];
  onChangeName: ForecastNameProps['onChange'];
  onChangeSourceForecast: ForecastSourceForecastProps['onChange'];
  revalidate: () => void;
}

export const CreateNewBudgetForm = ({
  errors = {},
  datasource,
  excelFileError,
  excelTemplateLink,
  fileUploadStatus,
  forecasts,
  from,
  isLoadingExcelTemplate,
  maxDate,
  minDate,
  name,
  sourceForecast,
  onChangeDatasource,
  onChangeFile,
  onChangeFrom,
  onChangeName,
  onChangeSourceForecast,
  revalidate
}: CreateNewBudgetFormProps) => {
  const intl = useIntl();

  return (
    <Box className={styles.carbonFormFixes}>
      <WizardRequiredFieldInfo />
      <ForecastName
        value={name}
        onChange={onChangeName}
        label={intl.formatMessage({ id: 'predict.create_forecast.budget_name', defaultMessage: 'Budget name' })}
        error={errors.name?.message}
        onBlur={revalidate}
      />
      <ForecastFrom
        maxDate={maxDate}
        minDate={minDate}
        value={from}
        onChange={onChangeFrom}
        label={intl.formatMessage({ id: 'common.start_date', defaultMessage: 'Start date' })}
        error={errors.from?.message}
        onBlur={revalidate}
      />

      <IconMessage mb={3}>
        {intl.formatMessage({
          id: 'predict.create_forecast.duration_is_one_year_from_start_date',
          defaultMessage: 'Duration is 1 year from the start date'
        })}
      </IconMessage>
      <ForecastDatasource
        type="budget"
        value={datasource}
        onChange={onChangeDatasource}
        error={errors.datasource?.message}
        onBlur={revalidate}
      />
      {datasource === NewForecastDataSource.FromDuplicate && (
        <ForecastSourceForecast
          type="budget"
          value={sourceForecast ?? ''}
          onChange={onChangeSourceForecast}
          error={errors.sourceForecast?.message}
          forecasts={forecasts}
          onBlur={revalidate}
        />
      )}

      {datasource === NewForecastDataSource.FromExcelUpload && (
        <ExcelUploadPanel
          error={excelFileError}
          isLoading={isLoadingExcelTemplate}
          onChange={onChangeFile}
          templateLink={excelTemplateLink}
          uploadStatus={fileUploadStatus}
        />
      )}
    </Box>
  );
};
